import ViewAccountsModal from './view-accounts/ViewAccountsModal'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Layout, MenuProps, notification, Typography } from 'antd'
import { colorText } from 'src/app/themes/primaryThemeDefault'
import { EyeIcon, PenIcon, UserIcon } from 'src/assets/icons'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { useClientSetupFilters } from './ClientSetupFiltersContext'

import './index.scss'
import { GetClientListPayload, IViewAccount } from 'src/app/shared/models/clientSetup.model'
import { getAccountList, getClientList } from 'src/app/shared/services/ClientSetupService'
import { useClientSetup } from './useClientSetup'
import { TABLE_NAME } from 'src/app/shared/constants/tableName.constants'
import { useRolePermission } from 'src/app/shared/hooks/useRolePermission'
import { EPermissions, ESubModules } from 'src/app/shared/models/roleAndPermission.model'
import NewCommonTable from 'src/app/shared/components/common/new-table'
import { YesNoTag } from '../../feature-business-administration/bank-user-setup/components/TagComponents'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import { ShortenListTags } from 'src/app/shared/components/common/tag/ShortenListTags'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
import { outerTableRef } from 'src/app/shared/components/common/new-table/components/TableProvider'
import { IGetExportData } from 'src/app/shared/components/common/new-table/table-setting.model'
import ActiveQuickFilter from './ActiveQuickFilter'
import { settingHeightTableWithFilter } from 'src/app/shared/constants/common.constants'

const ClientSetup: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { state } = location
  const { userPermissions } = useRolePermission()
  const [api, contextHolder] = notification.useNotification()
  const { saveSearchParams, restoreSearchParams } = useClientSetupFilters()

  // Initialize defaultParams from sessionStorage
  const [defaultParams] = useState<any>(() => {
    const savedParams = restoreSearchParams()
    return savedParams || {}
  })

  const [isViewAccountsModalOpen, setIsViewAccountsModalOpen] = useState<boolean>(false)
  const [clientNameSelected, setClientNameSelected] = useState('')
  const [listAccount, setListAccount] = useState<IViewAccount[]>()
  const { clientList, totalRecords, isLoading, isActive, setIsActive, getClientListData } = useClientSetup()
  const [isLoadingAccountList, setIsLoadingAccountList] = useState<boolean>(false)

  const getDataAccountList = useCallback(async (clientId: string) => {
    setIsLoadingAccountList(true)
    try {
      const data = await getAccountList(clientId, true)
      setListAccount(data.data)
    } catch (error) {
      console.log('ERROR: ', error)
    } finally {
      setIsLoadingAccountList(false)
    }
  }, [])

  const handleViewListAccount = (rowData: any) => {
    getDataAccountList(rowData.id)
    setClientNameSelected(rowData.clientName)
    setIsViewAccountsModalOpen(true)
  }
  const handleCloseModal = () => {
    setIsViewAccountsModalOpen(false)
  }

  const getItems = useCallback(
    (rowData: any): MenuProps['items'] => {
      return [
        userPermissions[ESubModules.CLIENT_SETUP]?.includes(EPermissions.VIEW_ITEMS)
          ? {
              key: '1',
              label: (
                <Typography.Text style={{ fontSize: '14px' }}>
                  {t('ClientSetup:addNewClient.list.actions.viewAccountList')}
                </Typography.Text>
              ),
              icon: <UserIcon />,
              onClick: () => {
                handleViewListAccount(rowData)
              },
            }
          : null,
        userPermissions[ESubModules.CLIENT_SETUP]?.includes(EPermissions.ADD_EDIT_ITEMS)
          ? {
              key: '2',
              label: (
                <Typography.Text style={{ fontSize: '14px' }}>
                  {t('ClientSetup:addNewClient.list.actions.editClientSetup')}
                </Typography.Text>
              ),
              icon: <PenIcon />,
              onClick: () => {
                if (rowData?.id) navigate(`${menuUrls.editClientSetup}/${rowData?.id}`)
              },
            }
          : null,
        userPermissions[ESubModules.CLIENT_SETUP]?.includes(EPermissions.VIEW_ITEMS)
          ? {
              key: '3',
              label: (
                <Typography.Text style={{ fontSize: '14px', color: colorText }}>
                  {t('ClientSetup:addNewClient.list.actions.viewClientSetup')}
                </Typography.Text>
              ),
              icon: <EyeIcon />,
              onClick: () => {
                if (rowData?.id) navigate(`${menuUrls.viewClientSetup}/${rowData?.id}`)
              },
            }
          : null,
      ]
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, navigate],
  )

  const conditionalFields = useMemo(
    () => ['autoLoadIssuedCheckFiles', 'displayIssuedPayee', 'displayNotes', 'payeeMatch'],
    [],
  )

  const generateCustomColumnDefs = useCallback(
    (colDefs: Array<TableColumnDefs>): Array<TableColumnDefs> =>
      colDefs.map((item, index) => {
        const { colId } = item
        if (colId === 'defaultACHExceptionCutoffTime') {
          item.valueGetter = (params: any) => params.data?.defaultAchExceptionCutoffTime
        }
        if (colId === 'issuedCheckFileFormats') {
          item.cellRenderer = (props: any) => {
            const { data } = props
            const issuedCheckFileFormats = data?.issuedCheckFileFormats
            const columnWidth = props.eGridCell ? props.eGridCell.clientWidth : props.column.getActualWidth()

            return issuedCheckFileFormats?.length ? (
              <ShortenListTags data={issuedCheckFileFormats} width={columnWidth} />
            ) : (
              <Typography />
            )
          }
        }
        if (conditionalFields?.includes(colId))
          item.cellRenderer = (props: any) => {
            const { data } = props
            return <YesNoTag status={data?.[colId]} />
          }
        if (
          ['archivedDate', 'createdAt', 'autoLoadIssuedCheckFiles', 'payeeMatch', 'displayIssuedPayee'].includes(colId)
        ) {
          item.dataType = 'date'
        }
        // if (['clientNumber'].includes(colId)) {
        //   item.dataType = 'number'
        // }
        return item
      }),
    [conditionalFields],
  )

  const handleAddRecord = () => {
    navigate(menuUrls.addClientSetup)
  }

  const handleSearch = useCallback(
    async (payload: Partial<GetClientListPayload>) => {
      const paramsToSave = {
        ...payload,
        isSearchAllColumns: (payload.searchColumns?.length || 0) > 1,
      }
      saveSearchParams(paramsToSave)
      await getClientListData(payload)
    },
    [getClientListData, saveSearchParams],
  )

  useEffect(() => {
    if (state?.clientSetup?.message) {
      api.success({
        message: <RichTextField richText={state?.clientSetup?.message} />,
      })
    }

    window.history.replaceState({}, '')
  }, [api, state?.clientSetup])

  // Restore saved search params when component mounts
  useEffect(() => {
    const savedParams = restoreSearchParams()
    
    if (savedParams) {
      const { isSearchAllColumns, searchTerm } = savedParams
      setTimeout(() => {
        const formSearchColumns = isSearchAllColumns ? 'All' : (savedParams.searchColumns?.[0] || 'All')
        
        outerTableRef.current?.setSearchFormValues?.(
          searchTerm || '',
          formSearchColumns
        )
        if (searchTerm) {
          outerTableRef.current?.setSearchOpen?.(true)
        }
      }, 300)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layout.Content style={{ padding: 24 }}>
      {contextHolder}

      <NewCommonTable
        ref={outerTableRef}
        pageName={TABLE_NAME.CLIENT_SETUP}
        tableSettingName={TABLE_NAME.CLIENT_SETUP}
        rowData={clientList}
        total={totalRecords}
        translationNamespace='ClientSetup:addNewClient.list'
        displayAddBtn={userPermissions[ESubModules.CLIENT_SETUP]?.includes(EPermissions.ADD_EDIT_ITEMS)}
        displayDownloadBtn={userPermissions[ESubModules.CLIENT_SETUP]?.includes(EPermissions.VIEW_ITEMS)}
        loading={isLoading}
        handleAddRecord={handleAddRecord}
        actionDropdownItems={getItems}
        handleSearch={handleSearch as any}
        handleDownload={async (payload) => await getClientList({ ...payload, params: { isActive } } as any)}
        generateCustomColumnDefs={generateCustomColumnDefs}
        defaultParams={defaultParams}
        customFilter={(
          props: Partial<IGetExportData> & {
            loading?: boolean
          },
        ) => <ActiveQuickFilter props={{ ...props, isActive, outerTableRef, setIsActive, handleSearch }} />}
        settingHeightTable={settingHeightTableWithFilter}
      />

      <ViewAccountsModal
        isOpen={isViewAccountsModalOpen}
        onClose={handleCloseModal}
        clientName={clientNameSelected}
        listAccount={listAccount}
        loading={isLoadingAccountList}
      />
    </Layout.Content>
  )
}

export default ClientSetup
